/**
 * Web App Router for /web/* routes
 * Separate from Telegram Mini App
 */
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import { ThemeProvider } from './ThemeContext';

// Components
import Login from './components/web/Login';
import Register from './components/web/Register';
import Explore from './components/web/Explore';
import Chat from './components/Chat';
import WebChat from './components/web/Chat';
import CreateCharacter from './components/web/CreateCharacter';
import AdminModeration from './components/web/AdminModeration';
import MyCharacters from './components/web/MyCharacters';
import PromoCode from './components/web/PromoCode';
import Layout from './components/web/Layout';
import TestPage from './components/web/TestPage';
import BalanceStatus from './components/web/BalanceStatus';
import Prices from './components/web/Prices';
import Gallery from './components/web/Gallery';
import MyChats from './components/web/MyChats';
import Policies from './components/web/Policies';
import PolicyPage from './components/web/PolicyPage';
import EditCharacter from './components/web/EditCharacter';
import GroupChat from './components/web/GroupChat';
import DailyBonusModal from './components/web/DailyBonusModal';
import GlobalChat from './components/web/GlobalChat';
import Forum from './components/web/Forum';

// API Configuration
const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? 'https://hotbump.online/api'
  : 'http://localhost:8013/api';

axios.defaults.baseURL = API_BASE_URL;

// Auth context
export const AuthContext = React.createContext();

function WebApp() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [language, setLanguage] = useState('en'); // Default English for US market
  const [error, setError] = useState(null);
  const [showDailyBonus, setShowDailyBonus] = useState(false);

  // Load user from token
  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      loadUser();
    } else {
      setLoading(false);
    }
  }, []);

  // Load saved language (default to English)
  useEffect(() => {
    const savedLang = localStorage.getItem('preferredLanguage');
    if (savedLang && (savedLang === 'en' || savedLang === 'ru')) {
      setLanguage(savedLang);
    } else {
      // Default to English
      setLanguage('en');
      localStorage.setItem('preferredLanguage', 'en');
    }
  }, []);

  const loadUser = async () => {
    try {
      const response = await axios.get('/auth/me');
      setUser(response.data);
      setError(null);
      
      // Check for daily bonus availability after user is loaded
      if (response.data) {
        checkDailyBonus();
      }
    } catch (error) {
      console.error('Failed to load user:', error);
      localStorage.removeItem('auth_token');
      delete axios.defaults.headers.common['Authorization'];
      setUser(null);
      // Don't set error for 401 - user just not logged in
      if (error.response?.status !== 401) {
        setError('Failed to load user data');
      }
    } finally {
      setLoading(false);
    }
  };

  const checkDailyBonus = async () => {
    try {
      const response = await axios.get('/web/daily-bonus/status');
      if (response.data.available) {
        // Show modal after a short delay for better UX
        setTimeout(() => {
          setShowDailyBonus(true);
        }, 1000);
      }
    } catch (error) {
      // Silently fail - daily bonus check is not critical
      console.debug('Daily bonus check failed:', error);
    }
  };

  const updateUser = (userData) => {
    setUser(userData);
  };

  const handleDailyBonusClaimSuccess = (data) => {
    // Update user balance from claim response
    if (user) {
      setUser({
        ...user,
        token_balance: data.new_balance
      });
    }
  };

  const login = (token, userData) => {
    localStorage.setItem('auth_token', token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('auth_token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  };

  const changeLanguage = (lang) => {
    setLanguage(lang);
    localStorage.setItem('preferredLanguage', lang);
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#111827', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ color: 'white', fontSize: '1.25rem' }}>Loading HotBump Web App...</div>
      </div>
    );
  }

  // Error boundary fallback
  if (error && !user) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#111827', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ color: '#f87171', marginBottom: '1rem' }}>{error}</div>
          <button
            onClick={() => window.location.reload()}
            style={{ padding: '0.5rem 1rem', backgroundColor: '#2563eb', color: 'white', borderRadius: '0.375rem', border: 'none', cursor: 'pointer' }}
          >
            Reload
          </button>
        </div>
      </div>
    );
  }

  return (
    <ThemeProvider>
      <AuthContext.Provider value={{ user, login, logout, language, changeLanguage, updateUser }}>
        <Router>
          <Routes>
            {/* Test route to verify WebApp is working */}
            <Route path="/web/test" element={<TestPage />} />
            
            {/* Public routes - no auth required */}
            <Route path="/web/login" element={<Login />} />
            <Route path="/web/register" element={<Register />} />
            
            {/* Public and protected routes */}
            <Route path="/web" element={<Layout />}>
            <Route index element={<Navigate to="/web/explore" replace />} />
            {/* Public route - no auth required */}
            <Route path="explore" element={<Explore />} />
            {/* Public route */}
            <Route path="gallery" element={<Gallery />} />
            {/* Public route */}
            <Route path="prices" element={<Prices />} />
            {/* Global Chat - requires auth */}
            <Route path="global-chat" element={user ? <GlobalChat /> : <Navigate to="/web/login" replace />} />
            {/* Forum - public route (placeholder) */}
            <Route path="forum" element={<Forum />} />
            {/* Public routes - Policies */}
            <Route path="policies" element={<Policies />} />
            <Route path="policies/:policyId" element={<PolicyPage />} />
            {/* Protected routes - require auth */}
            <Route path="my-chat" element={user ? <MyChats /> : <Navigate to="/web/login" replace />} />
            <Route path="chat/:characterId" element={<WebChat />} />
            <Route path="group-chat/:chatId" element={user ? <GroupChat /> : <Navigate to="/web/login" replace />} />
            <Route path="create" element={user ? <CreateCharacter /> : <Navigate to="/web/login" replace />} />
            <Route path="characters" element={user ? <MyCharacters /> : <Navigate to="/web/login" replace />} />
            <Route path="characters/:characterId/edit" element={user ? <EditCharacter /> : <Navigate to="/web/login" replace />} />
            <Route path="promo" element={user ? <PromoCode /> : <Navigate to="/web/login" replace />} />
            <Route path="balance" element={user ? <BalanceStatus /> : <Navigate to="/web/login" replace />} />
            <Route path="admin/moderation" element={<AdminModeration />} />
          </Route>
          
          {/* Default redirect - always to explore (public) */}
          <Route path="/web/*" element={<Navigate to="/web/explore" replace />} />
        </Routes>
      </Router>
      
      {/* Daily Bonus Modal */}
      {showDailyBonus && user && (
        <DailyBonusModal
          onClose={() => setShowDailyBonus(false)}
          onClaimSuccess={handleDailyBonusClaimSuccess}
        />
      )}
    </AuthContext.Provider>
    </ThemeProvider>
  );
}

export default WebApp;

